import React, { Component, PropTypes } from 'react'
import Modal from 'react-modal'
import { reduxForm } from 'redux-form'
import { Table, Tr, Td } from 'components/reactable'
import { get } from 'lodash/object'
import { isEmpty, isArray, cloneDeep } from 'lodash/lang'
import cn from 'classnames'
import Input from '../../../input'
import { stringToBoolean } from 'lib/helpers'
import InfoModal from 'components/generalComponents/infoModal'
import TrashIcon from 'resources/images/a-trash_1x_ovbBlue.svg'
import { FormField } from '../../../shared/form-field'
import { intl, translate } from './translation'
import { columns as tableColumns } from './columns'
import { Item, customStyles } from './helpers'
import { validateClientProfileDataFormValue } from './validation'
import styles from './ClientProfileDataFormList.scss'

const DefaultChangedAlert = ({ isEmailsList, initialValue, value }) => {
    const initialDefault = initialValue.find((item) => stringToBoolean(item.default))
    const currentDefault = value.find((item) => stringToBoolean(item.default))
    const isSameDefault = get(initialDefault, 'email') === get(currentDefault, 'email')

    if (!isEmailsList || isSameDefault) {
        return null
    }

    return (
        <div className={styles.alertContainer}>
            <svg
                className={styles.icon}
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
            >
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"/>
            </svg>
            <div>
                <span className={styles.highlight}>{translate(intl.attention)}</span>
                {translate(intl.defaultEmailChangedMessage)}
            </div>
        </div>
    )
}
DefaultChangedAlert.propTypes = {
    value: PropTypes.array,
    isEmailsList: PropTypes.bool,
    initialValue: PropTypes.array,
}

@reduxForm({
    form: 'clientProfileDataFormValue',
    fields: ['email', 'number'], // use for the email and phone number fields
    validate: validateClientProfileDataFormValue,
    initialValues: { email: '', number: '' },
})
class ModalContentPopup extends Component { // eslint-disable-line react/prefer-stateless-function
    render() {
        const {fields, invalid, pristine, value, handleToggleModal, handleSubmit, handleAddItem } = this.props
        const isDisabled = pristine || fields[value].value === '' || invalid

        return (
            <form onSubmit={handleSubmit(handleAddItem)} className={styles.clientModalContent}>
                <h4 className={styles.title}>{translate(intl.addNewItem(value))}</h4>
                <label>{translate(intl.value(value))}</label>
                <FormField field={fields[value]}>
                    <Input
                        type="text"
                        name={value}
                        value={fields[value].value}
                        onBlur={fields[value].onBlur} // eslint-disable-line react/jsx-handler-names
                        onChange={fields[value].onChange} // eslint-disable-line react/jsx-handler-names
                    />
                </FormField>
                <div className={styles.clientModalButtons}>
                    <button type="button" onClick={handleToggleModal} className={styles.cancel}>{translate(intl.cancel)}</button>
                    <button type="submit" className={cn(styles.next, !isDisabled && styles.enabled)} disabled={isDisabled}>
                        {translate(intl.add)}
                    </button>
                </div>
            </form>
        )
    }
}
ModalContentPopup.propTypes = {
    value: PropTypes.oneOf(['email', 'number']),
    handleAddItem: PropTypes.func,
    handleToggleModal: PropTypes.func,
    fields: PropTypes.object,
    invalid: PropTypes.bool,
    pristine: PropTypes.bool,
    handleSubmit: PropTypes.func,
}

/**
 * This component is a list of emails or phone numbers used in the client profile form
 * @param {(Object[]|undefined)} emails - list of emails
 * @param {(Object[]|undefined)} phoneNumbers - list of phone numbers
 * @param {Function} onChange - function to call when the list changes
 * @returns {Component}
 */
export class ClientProfileDataFormList extends Component {
    constructor(props) {
        super(props)
        this.state = {
            modalIsOpen: false,
            informModalOpen: false,
            isEmailsList: isArray(props.emails),
        }
    }

    toggleModal() {
        this.setState({ modalIsOpen: !this.state.modalIsOpen })
    }

    setMobile(phoneNumber) {
        const { phoneNumbers, onChange } = this.props
        const index = phoneNumbers.findIndex((item) => item.number === phoneNumber)
        if (index === -1) {
            return // do nothing if the phone number is not found
        }
        const newPhoneNumbers = cloneDeep(phoneNumbers) // ensure immutability
        newPhoneNumbers[index].mobile = !newPhoneNumbers[index].mobile
        onChange(newPhoneNumbers)
    }

    setUniqueDefault(item) {
        const { onChange } = this.props
        const { value, data: list } = this.setListFromProps()
        const index = list.findIndex((listItem) => listItem[value] === item)
        if (index === -1) {
            return // do nothing if the item is not found
        }
        const newItems = cloneDeep(list) // ensure immutability
        newItems.forEach((newItem, newIndex) => {
            newItem.default = newIndex === index // set the default to true for the selected item
        })
        onChange(newItems)
    }

    handleAddItem(item) { // item is the email or phone number
        const { onChange } = this.props
        const { value, data: list } = this.setListFromProps()
        const newItem = new Item(value)
        newItem[value] = item[value] // set the value of the new email or phone number
        if (isArray(list) && list.length === 0) {
            newItem.default = true // set the default to true if the list is empty
        }
        const newList = list.concat([newItem]) // add the new email to the end of the list
        onChange(newList)
        this.toggleModal()
    }

    handleRemoveItem(item) { // item is the email or phone number
        const { onChange } = this.props
        const { value, data: list } = this.setListFromProps()

        const indexToRemove = list.findIndex((listItem) => listItem[value] === item)
        if (indexToRemove === -1) {
            return // do nothing if the item is not found
        }
        if (get(list[indexToRemove], 'default', true) === 'true') { // set default to true in case index is not found and show the modal
            this.setState({informModalOpen: true}) // prevent to delete the default email
            return
        }
        const newList = cloneDeep(list) // ensure immutability
        newList.splice(indexToRemove, 1) // remove the email from the list
        onChange(newList)
    }

    setListFromProps() {
        const { emails, phoneNumbers } = this.props
        const { isEmailsList } = this.state
        const data = isEmailsList ? emails : phoneNumbers
        const value = isEmailsList ? 'email' : 'number'
        return { value, data }
    }

    render() {
        const { informModalOpen, modalIsOpen, isEmailsList } = this.state
        const { value, data } = this.setListFromProps()
        const { initialValue } = this.props
        const columns = cloneDeep(tableColumns)

        return (
            <div className={styles.moduleSection}>
                <DefaultChangedAlert
                    value={data}
                    isEmailsList={isEmailsList}
                    initialValue={initialValue}
                />
                <div className={styles.clientsTableContainer}>
                    {!isEmpty(data) && (
                        <Table
                            itemsPerPage={50}
                            topPagination=""
                            filterable={[]}
                            columns={columns[value]}
                            sortable={[value]}
                            defaultSort={{ column: value, direction: 'asc' }}
                        >
                            {data.map((item, index) =>
                                <Tr className="centered" key={item[value] + index} id={item[value] + index}>
                                    <Td column={value}>{item[value]}</Td>
                                    {!isEmailsList && (
                                        <Td column="mobile">
                                            <input
                                                type="checkbox"
                                                value={stringToBoolean(item.mobile)}
                                                checked={stringToBoolean(item.mobile)}
                                                name={value + 'Mobile' + index}
                                                onBlur={() => false}
                                                onChange={this.setMobile.bind(this, item[value])}
                                            />
                                        </Td>
                                    )}
                                    <Td column="default">
                                        <input
                                            type="radio"
                                            value={item.default}
                                            checked={stringToBoolean(item.default)}
                                            name={value + 'Default'}
                                            onBlur={() => false}
                                            onChange={this.setUniqueDefault.bind(this, item[value])}
                                        />
                                    </Td>
                                    <Td column="removeItem">
                                        <button
                                            type="button"
                                            className={styles.removeFieldsButton}
                                            onClick={this.handleRemoveItem.bind(this, item[value])}
                                        >
                                            <img src={TrashIcon}/>
                                        </button>
                                    </Td>
                                </Tr>
                            )}
                        </Table>
                    )}
                    <button type="button" className={styles.addFieldButton} onClick={this.toggleModal.bind(this)}>+</button>
                </div>
                <Modal isOpen={modalIsOpen} style={customStyles} contentLabel="Modal">
                    <ModalContentPopup
                        value={value}
                        handleAddItem={this.handleAddItem.bind(this)}
                        handleToggleModal={this.toggleModal.bind(this)}
                    />
                </Modal>
                <InfoModal
                    isOpen={Boolean(informModalOpen)}
                    title={translate(intl.modalTitle(value))}
                    content={translate(intl.modalContent(value))}
                    onCancel={() => {}}
                    onConfirmation={() => {this.setState({informModalOpen: false})}}
                    cancelLabel={translate(intl.cancel)}
                    confirmationLabel="ok"
                    options={{noCancelButton: true}}
                />
            </div>
        )
    }
}
ClientProfileDataFormList.propTypes = {
    emails: PropTypes.arrayOf(PropTypes.shape({
        email: PropTypes.string,
        default: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    })),
    phoneNumbers: PropTypes.arrayOf(PropTypes.shape({
        number: PropTypes.string,
        default: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        mobile: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    })),
    initialValue: PropTypes.array,
    onChange: PropTypes.func,
}




// WEBPACK FOOTER //
// ./components/easinsure/client-profile/client-profile-form/ClientProfileDataFormList.jsx